// Walks doc.json blocks → React tree. Every rendered block carries
// `data-block-id` so the IntersectionObserver in `useActiveGlossaryTerms` can
// attach to it. Unknown block kinds render `null` (forward-compat invariant).

import type { Block, DocJSON, EquationEntry, InlineRun } from "../data/types";
import type { CanonicalResolver } from "./canonicalResolver";
import { EquationBlock } from "./EquationBlock";
import { EquationSymbolHoverProvider } from "./equationSymbolHover";
import { Math } from "./Math";

export interface ProseProps {
  doc: DocJSON;
  // PRD 07 — per-equation metadata indexed by block_id. Optional so
  // zero-equation docs and pre-extraction reads keep rendering as plain
  // KaTeX (forward-compat invariant).
  equations?: EquationEntry[];
  // 00_processor §Pass 2 — passed down to EquationBlock so symbol cards
  // prefer the canonical Definition over the per-doc one.
  canonicalResolver?: CanonicalResolver;
}

export function Prose({ doc, equations, canonicalResolver }: ProseProps) {
  const equationByBlockId = new Map<string, EquationEntry>(
    (equations ?? []).map((eq) => [eq.block_id, eq]),
  );
  // The provider is doc-scoped and renders no DOM — it is what makes symbol
  // hover cross equation blocks (PRD 10 Phase B deliverable 3).
  return (
    <EquationSymbolHoverProvider>
      {doc.sections.map((section) => (
        <section
          key={section.id}
          data-section-id={section.id}
          className="maisight-section"
        >
          {section.heading ? (
            <h2 className="maisight-section-head">{section.heading}</h2>
          ) : null}
          {section.blocks.map((b) => (
            <BlockView
              key={b.id}
              block={b}
              equation={equationByBlockId.get(b.id)}
              docId={doc.doc_id}
              canonicalResolver={canonicalResolver}
            />
          ))}
        </section>
      ))}
    </EquationSymbolHoverProvider>
  );
}

function BlockView({
  block,
  equation,
  docId,
  canonicalResolver,
}: {
  block: Block;
  equation?: EquationEntry;
  docId: string;
  canonicalResolver?: CanonicalResolver;
}) {
  switch (block.kind) {
    case "paragraph":
      return (
        <p data-block-id={block.id} className="maisight-p">
          {renderInline(block.inline ?? [], block.id)}
        </p>
      );
    case "list": {
      // Lists may carry `items` (array of inline runs per item) in `meta`,
      // or fall back to a single inline run flattened by line. Forward-compat.
      const ordered = block.meta?.ordered === true;
      const items =
        (block.meta?.items as InlineRun[][] | undefined) ??
        (block.inline ? [block.inline] : []);
      const Tag = (ordered ? "ol" : "ul") as "ol" | "ul";
      return (
        <Tag data-block-id={block.id} className="maisight-list">
          {items.map((runs, i) => (
            <li key={i}>{renderInline(runs, block.id)}</li>
          ))}
        </Tag>
      );
    }
    case "code":
      return (
        <pre data-block-id={block.id} className="maisight-code">
          <code>{block.raw ?? ""}</code>
        </pre>
      );
    case "blockquote":
      return (
        <blockquote data-block-id={block.id} className="maisight-blockquote">
          {renderInline(block.inline ?? [], block.id)}
        </blockquote>
      );
    case "table": {
      const rows = (block.meta?.rows as InlineRun[][][] | undefined) ?? [];
      const head = (block.meta?.head as InlineRun[][] | undefined) ?? null;
      return (
        <table data-block-id={block.id} className="maisight-table">
          {head ? (
            <thead>
              <tr>
                {head.map((cell, i) => (
                  <th key={i}>{renderInline(cell, block.id)}</th>
                ))}
              </tr>
            </thead>
          ) : null}
          <tbody>
            {rows.map((row, i) => (
              <tr key={i}>
                {row.map((cell, j) => (
                  <td key={j}>{renderInline(cell, block.id)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      );
    }
    case "equation":
      // PRD 07: when an equation entry exists for this block, render the
      // per-symbol-annotated panel. Else fall back to plain KaTeX (the
      // doc was processed before equation extraction ran, or the LLM
      // returned nothing for it).
      return equation ? (
        <EquationBlock
          equation={equation}
          docId={docId}
          canonicalResolver={canonicalResolver}
        />
      ) : (
        <Math display raw={block.raw ?? ""} blockId={block.id} />
      );
    case "figure": {
      const src = block.meta?.src as string | undefined;
      const alt = (block.meta?.alt as string | undefined) ?? "";
      const caption = block.inline ?? [];
      return (
        <figure data-block-id={block.id} className="maisight-figure">
          {src ? <img src={src} alt={alt} /> : null}
          {caption.length > 0 ? (
            <figcaption>{renderInline(caption, block.id)}</figcaption>
          ) : null}
        </figure>
      );
    }
    case "callout": {
      const tone = (block.meta?.tone as string | undefined) ?? "note";
      return (
        <aside
          data-block-id={block.id}
          className={`maisight-callout maisight-callout--${tone}`}
        >
          {renderInline(block.inline ?? [], block.id)}
        </aside>
      );
    }
    case "mdx_component":
      // v1 renders MDX components as opaque blocks; the raw JSX is preserved
      // for fidelity. See 01_renderer.md "Open questions".
      return (
        <pre
          data-block-id={block.id}
          className="maisight-opaque maisight-opaque--mdx"
        >
          <code>{block.raw ?? ""}</code>
        </pre>
      );
    case "openapi_resource":
      // OpenAPI resources render as a labelled opaque block in v1; the
      // dedicated ApiResource view is part of the richer renderer pass.
      return (
        <section
          data-block-id={block.id}
          className="maisight-openapi-resource"
        >
          <header className="maisight-openapi-resource-head">
            {(block.meta?.method as string | undefined) ?? ""}{" "}
            {(block.meta?.path as string | undefined) ?? block.id}
          </header>
          {block.inline ? (
            <p className="maisight-openapi-resource-desc">
              {renderInline(block.inline, block.id)}
            </p>
          ) : null}
        </section>
      );
    default:
      // Forward-compat invariant #6: unknown block kinds render nothing.
      return null;
  }
}

function renderInline(runs: InlineRun[], blockId?: string) {
  // PRD 10 Phase B — inline math gets its own hover envelope. The
  // equation_id matches the parse-stage formula `{block_id}-i{N}-eq`
  // (backend/parse/equations.py::inline_equation_id) so the
  // doc-level coordinator can address inline shells the same way it
  // addresses display blocks. `mathIndex` counts only math runs in
  // this block — re-tokenizing text doesn't renumber the IDs
  // (invariant #1, same rule the backend follows).
  let mathIndex = 0;
  return runs.map((r, i) => {
    switch (r.kind) {
      case "text":
        return <span key={i}>{r.text ?? ""}</span>;
      case "code":
        return <code key={i}>{r.text ?? ""}</code>;
      case "math": {
        const eqId = blockId ? `${blockId}-i${mathIndex}-eq` : undefined;
        mathIndex++;
        return (
          <span
            key={i}
            className="maisight-inline-equation"
            data-equation-id={eqId}
            data-block-id={blockId}
          >
            <Math display={false} raw={r.text ?? ""} />
          </span>
        );
      }
      case "link":
        return (
          <a key={i} href={r.href ?? "#"}>
            {r.text ?? ""}
          </a>
        );
      case "emph":
        return <em key={i}>{r.text ?? ""}</em>;
      case "strong":
        return <strong key={i}>{r.text ?? ""}</strong>;
      case "cite":
        return (
          <cite key={i} data-cite-id={r.meta?.id as string | undefined}>
            {r.text ?? ""}
          </cite>
        );
      default:
        return <span key={i}>{r.text ?? ""}</span>;
    }
  });
}
